import { createReducer } from '@reduxjs/toolkit'

import { cloneDeep } from 'lodash'

import { arrayToObject } from 'helpers/Functions'
import { TAccount, TAccountRoleApi } from 'types/GlobalAccount'

import * as accountActions from '../../actions/Accounts/actions'
import * as accountRoleActions from '../../actions/AccountRoles/actions'
import * as companyGroupActions from '../../actions/CompanyGroups/actions'
import * as dashboardGroupActions from '../../actions/DashboardGroups/actions'
import * as selfActions from '../../actions/Self/actions'
import * as authenticationActions from '../../actions/Authentication/actions'
import { EReduxState } from 'redux/utils/State'

export type AccountBody = {
  [key: string]: TAccount
}

export type RoleReducerType = { [roleName: string]: TAccountRoleApi }

export interface AccountReducerType {
  data: AccountBody
  state: EReduxState
  roles: RoleReducerType
  rolesState: EReduxState
}

// Initial state
const INITIAL_STATE: AccountReducerType = {
  data: {},
  state: EReduxState.IDLE,
  roles: {},
  rolesState: EReduxState.IDLE
}

// Reducer
const accountReducer = createReducer(INITIAL_STATE, (builder) => {
  builder
    .addCase(accountActions.getAll, (state) => {
      return {
        ...state,
        state: EReduxState.LOADING
      }
    })
    .addCase(accountActions.getAllSuccess, (state, action) => {
      const { payload } = action

      return {
        ...state,
        data: arrayToObject(payload.data) as AccountBody,
        state: EReduxState.SUCCESS
      }
    })
    .addCase(accountActions.getAllFailed, (state) => {
      return {
        ...state,
        state: EReduxState.ERROR
      }
    })
    .addCase(accountRoleActions.getAll, (state) => {
      return {
        ...state,
        rolesState: EReduxState.LOADING
      }
    })
    .addCase(accountRoleActions.getAllSuccess, (state, action) => {
      const { payload } = action
      const roles: RoleReducerType = {}

      payload.data.forEach((role) => {
        roles[role.name] = role
      })

      return {
        ...state,
        roles,
        rolesState: EReduxState.SUCCESS
      }
    })
    .addCase(accountRoleActions.getAllFailed, (state) => {
      return {
        ...state,
        rolesState: EReduxState.ERROR
      }
    })
    .addCase(accountActions.createSuccess, (state, action) => {
      state.data[action.payload.account.id] = action.payload.account
    })
    .addCase(accountActions.updateSuccess, (state, action) => {
      if (action.payload.account.id in state.data) {
        state.data[action.payload.account.id] = {
          ...state.data[action.payload.account.id],
          ...action.payload.account
        }
      }
    })
    .addCase(accountActions.destroySuccess, (state, action) => {
      if (action.payload.id in state.data) {
        delete state.data[action.payload.id]
      }
    })
    .addCase(dashboardGroupActions.inviteSuccess, (state, action) => {
      const { payload } = action

      if (payload.new_account) {
        return {
          ...state,
          data: {
            ...state.data,
            [payload.new_account.id]: payload.new_account
          }
        }
      }

      return state
    })
    .addCase(selfActions.updateSelfSuccess, (state, action) => {
      const { payload } = action

      return {
        ...state,
        data: {
          ...state.data,
          [payload.id]: {
            ...state.data[payload.id],
            email: payload.attributes.email,
            first_name: payload.attributes.first_name,
            last_name: payload.attributes.last_name
          }
        }
      }
    })
    .addCase(companyGroupActions.destroySuccess, (state, action) => {
      const { payload } = action

      const newData = cloneDeep(state.data)

      Object.keys(newData).forEach((id) => {
        if (newData[id].company_group_id === payload.id) {
          delete newData[id]
        }
      })

      return { ...state, data: newData }
    })
    .addCase(authenticationActions.signOut, () => INITIAL_STATE)
    .addDefaultCase((state) => state)
})

export default accountReducer
